import React, { useMemo } from 'react';
import '../../css/PreviewPDF.css';
import { Client, TripInfo, ItineraryItem } from '../../types/Quotation';
import PDFPage1 from './PDFPage1';
import PDFPage2 from './PDFPage2';
import PDFPage2Summary from './PDFPage2Summary';
import PDFPage2PaymentCalendar from './PDFPage2PaymentCalendar';
import PDFPage3 from './PDFPage3';
import PDFPage4 from './PDFPage4';
import { getPdfRenderTimestamp } from './pdfRenderTime';

type Addon = { name: string; price: number; currency: string; description: string };
type VisaDetail = {
  type: string;
  price: number;
  validity?: string;
  documents?: string[];
  timeRequired?: string;
  currency?: string;
};
type InsuranceDetail = { name: string; price: number; currency?: string; description?: string };

interface PreviewPDFProps {
  client: Client;
  tripInfo: TripInfo;
  itinerary: ItineraryItem[];
  quotationCreatedAt?: string;
  generalRules: string;
  highlights: string;
  highlightImages: string[];
  inclusionImages: string[];
  itineraryImages: string[];
  addons?: Addon[];
  visaDetails?: VisaDetail[];
  insuranceDetails?: InsuranceDetail[];
  exclusions?: string[];
  paymentCalendar?: {
    flightCost: number;
    hotelCost: number;
    cabCost: number;
    totalPackageCost: number;
    currency: 'INR' | 'USD';
  };
  mealPlan?: string;
  selectedHotelRoomCount?: number;
  selectedCab?: {
    cabType?: string;
    maxOccupancy?: number;
    transferType?: string;
  };
}

const PreviewPDF: React.FC<PreviewPDFProps> = ({
  client,
  tripInfo,
  itinerary,
  quotationCreatedAt,
  highlights,
  highlightImages,
  inclusionImages,
  itineraryImages,
  addons = [],
  visaDetails = [],
  insuranceDetails = [],
  exclusions = [],
  paymentCalendar,
  mealPlan,
  selectedHotelRoomCount,
  selectedCab,
}) => {
  const tripInfoWithInclusions = { ...tripInfo };
  const renderedAt = useMemo(() => getPdfRenderTimestamp(), []);

  return (
    <div className="preview-pdf">
      <PDFPage1
        client={client}
        tripInfo={tripInfoWithInclusions}
        itinerary={itinerary}
        highlights={highlights}
        renderedAt={renderedAt}
      />
      <PDFPage2
        highlightImages={highlightImages}
        inclusionImages={inclusionImages}
        itinerary={itinerary}
        addons={addons}
        visaDetails={visaDetails}
        insuranceDetails={insuranceDetails}
        inclusions={tripInfo.inclusions || []}
        tripInfo={tripInfoWithInclusions}
        mealPlan={mealPlan}
        selectedRoomCount={selectedHotelRoomCount}
        selectedCab={selectedCab}
        travelStartDate={tripInfo.startDate}
        renderedAt={renderedAt}
      />
      <PDFPage2Summary
        tripInfo={tripInfoWithInclusions}
        itinerary={itinerary}
        visaDetails={visaDetails}
        insuranceDetails={insuranceDetails}
        renderedAt={renderedAt}
      />
      <PDFPage3
        itinerary={itinerary}
        itineraryImages={itineraryImages}
        startDate={tripInfo.startDate}
        tripInfo={tripInfoWithInclusions}
        selectedRoomCount={selectedHotelRoomCount}
        renderedAt={renderedAt}
      />
      {paymentCalendar && (
        <PDFPage2PaymentCalendar
          createdAt={renderedAt}
          tripStartDate={tripInfo.startDate}
          flightCost={paymentCalendar.flightCost}
          hotelCost={paymentCalendar.hotelCost}
          cabCost={paymentCalendar.cabCost}
          totalPackageCost={paymentCalendar.totalPackageCost}
          currency={paymentCalendar.currency}
          renderedAt={renderedAt}
        />
      )}
      <PDFPage4 exclusions={exclusions} renderedAt={renderedAt} />
    </div>
  );
};

export default PreviewPDF;
